import React, { useEffect, useReducer, useState } from "react";
import { Button } from "components/Buttons/Buttons";
import { Dropdown, Menu, Modal, Select, Space, Table } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import useSettingsHeader from "hooks/useSettingsHeader";
import { useDispatch } from "react-redux";
import {
  createPointsValuePolicy,
  getAllPointsValuePolicies,
  updatePointsValuePolicy,
} from "services/PointsValuePolicy.service";
import SearchAndFilterColumns from "components/SearchAndFilterColumns/SearchAndFilterColumns";
import usePortal from "hooks/usePortal";
import PointsValuePolicyDrawer from "./components/PointsValuePolicyDrawer";

const reducer = (state, action) => {
  switch (action.type) {
    case "SET":
      return action.data;
    default:
      return state;
  }
};

function PointsValuePolicy() {
  const [showAddEditDrawer, setShowAddEditDrawer] = useState();
  const [data, localDispatch] = useReducer(reducer, []);

  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [activeFilter, setActiveFilter] = useState("active"); // all, active, inactive
  const [searchText, setSearchText] = useState("");

  const dispatch = useDispatch();

  const loadData = async () => {
    const result = await dispatch(getAllPointsValuePolicies());
    if (result?.status === "success") {
      localDispatch({
        type: "SET",
        data: result.data,
      });
    }
  };
  useEffect(() => {
    loadData();
  }, []);

  const handleAdd = (values) => {
    dispatch(createPointsValuePolicy(values)).then((result) => {
      if (result?.status === "success") {
        setShowAddEditDrawer();
        loadData();
      }
    });
  };

  const handleUpdate = (id, values) => {
    dispatch(updatePointsValuePolicy(id, values)).then((result) => {
      if (result?.status === "success") {
        setShowAddEditDrawer();
        loadData();
      }
    });
  };

  const handleToggleActive = (record) => {
    const { loyaltyPointValuePolicyId, pointValueInDollars, notes, isActive } =
      record;
    dispatch(
      updatePointsValuePolicy(loyaltyPointValuePolicyId, {
        pointValueInDollars,
        notes,
        isActive: !isActive,
      })
    ).then((result) => {
      if (result?.status === "success") {
        loadData();
      }
    });
  };

  const inactivateConfirmation = (record) => ({
    title: "Inactivate Points Value Policy",
    content: (
      <>Are you sure that you want to inactivate this points value policy?</>
    ),
    centered: true,
    maskClosable: true,
    onOk: (close) => {
      handleToggleActive(record);
      close();
    },
  });

  const activateConfirmation = (record) => ({
    title: "Activate Points Value Policy",
    content: (
      <>
        Are you sure that you want to activate this points value policy? This
        will automatically inactivate the currently active policy.
      </>
    ),
    centered: true,
    maskClosable: true,
    onOk: (close) => {
      handleToggleActive(record);
      close();
    },
  });

  const checkActiveFilter = (item) => {
    if (activeFilter === "all") return true;
    else if (activeFilter === "active" && item?.isActive) return true;
    else if (activeFilter === "inactive" && !item?.isActive) return true;
    return false;
  };

  const settingHeaderContent = useSettingsHeader(
    <Button
      rounded={true}
      onClick={() => {
        setShowAddEditDrawer(true);
      }}
    >
      Create Points Value Policy
    </Button>
  );

  const columns = [
    {
      title: "Point Value ($)",
      dataIndex: "pointValueInDollars",
      key: "pointValueInDollars",
      sorter: (a, b) => a.pointValueInDollars - b.pointValueInDollars,
      render: (value) => `$${Number(value).toFixed(2)}`,
      width: 200
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      render: (value) => value?.length > 50 ? `${value.substring(0, 50)}...` : value || "-",
    },
    {
      title: "Active",
      key: "active",
      width: 100,
      render: (data) => {
        if (data?.isActive) return "Active";
        return "Inactive";
      },
    },
    {
      title: "",
      key: "action",
      align: "right",
      width: 80,
      render: (record) => {
        return (
          <Dropdown
            overlay={
              <Menu style={{ borderRadius: 6, border: "2px solid #CDD5D8 " }}>
                <Menu.Item
                  onClick={() => setShowAddEditDrawer(record)}
                  key="edit"
                >
                  Edit
                </Menu.Item>
                <Menu.Item
                  key="toggle"
                  onClick={() => {
                    if (record?.isActive)
                      Modal.warning(inactivateConfirmation(record));
                    else Modal.warning(activateConfirmation(record));
                  }}
                >
                  {record?.isActive ? "Inactivate" : "Activate"}
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
            getPopupContainer={(triggerNode) => triggerNode?.parentNode}
          >
            <MoreOutlined style={{ fontSize: 18 }} />
          </Dropdown>
        );
      },
    },
  ];

  const searchAndFiltersContent = usePortal(
    "#settingHeader .settingsHeaderLeftSideAddon",
    <Space>
      <SearchAndFilterColumns
        columns={columns}
        hiddenColumns={hiddenColumns}
        setHiddenColumns={(columnTitles) => {
          setHiddenColumns(columnTitles);
        }}
        searchText={searchText}
        setSearchText={(text) => {
          setSearchText(text);
        }}
      />
      {/* <Select
        style={{ width: 120 }}
        value={activeFilter}
        onChange={(value) => {
          setActiveFilter(value);
        }}
        options={[
          {
            value: "all",
            label: "All",
          },
          {
            value: "active",
            label: "Active",
          },
          {
            value: "inactive",
            label: "Inactive",
          },
        ]}
      /> */}
    </Space>
  );

  return (
    <div>
      {settingHeaderContent}
      {searchAndFiltersContent}
      <Table
        rowKey={(data) => data.loyaltyPointValuePolicyId}
        tableLayout="auto"
        locale={{ emptyText: "No Points Value Policy Found" }}
        dataSource={data?.filter(
          (it) =>
            checkActiveFilter(it) &&
            (it?.notes ?? "")
              .toLowerCase()
              .includes(searchText.toLowerCase())
        )}
        columns={columns.filter((column) => {
          if (!hiddenColumns.includes(column.title)) return true;
          return false;
        })}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [15, 25, 50, 100, 250],
        }}
      />

      <PointsValuePolicyDrawer
        data={showAddEditDrawer}
        visible={showAddEditDrawer}
        onClose={() => {
          setShowAddEditDrawer();
        }}
        onSuccess={(values) => {
          const isEdit =
            showAddEditDrawer?.loyaltyPointValuePolicyId !== undefined;
          if (isEdit)
            handleUpdate(showAddEditDrawer?.loyaltyPointValuePolicyId, values);
          else handleAdd(values);
        }}
      />
    </div>
  );
}

export default PointsValuePolicy;
